import {
  cloneAssemblyDocument,
  isAssemblyPersistedDocument,
  listAssemblyDocumentDirectChildNodes,
} from './assembly-document-operations'
import type { AssemblyDocument } from './assembly-document-types'
import {
  createRepositoryPuppyNodeWhere as createRepositoryPuppyNodeWherePath,
  normalizeRepositoryPuppyPackageFileId,
  parseRepositoryPuppyNodeWhere as parseRepositoryPuppyNodeWherePath,
} from './repository-puppy-node-where-path'

/**
 * 同次导出根直系 occurrence 的桥接身份。
 */
export interface RepositoryPuppyRootOccurrenceIdentity {
  /** 运行项目树中的根直系节点 key。 */
  readonly sourceNodeKey: string
  /** 最终写入 `.puppy` assembly.json 的稳定节点 ID。 */
  readonly assemblyNodeId: string
}

/**
 * 规范并校验仓库内 `.puppy` 文件路径。
 *
 * 仓库协议固定使用相对 POSIX 路径；这里仅去除用户输入两端空白，不转换 Windows 分隔符，
 * 避免不同调用方对同一 packageFileId 形成不同解释。
 *
 * @param packageFileId 待校验的仓库文件路径。
 * @returns 去除两端空白后的合法相对路径。
 */
function normalizeRepositoryPackageFileId(packageFileId: string): string {
  return normalizeRepositoryPuppyPackageFileId(packageFileId)
}

/**
 * 创建仓库 `_assembly.json` 使用的 Puppy occurrence 定位路径。
 *
 * @param packageFileId 仓库内 `.puppy` 文件路径。
 * @param assemblyNodeId 包内稳定装配节点 ID。
 * @returns `<packageFileId>/<assemblyNodeId>` 形式的仓库定位路径。
 */
export function createRepositoryPuppyNodeWhere(
  packageFileId: string,
  assemblyNodeId: string,
): string {
  return createRepositoryPuppyNodeWherePath(packageFileId, assemblyNodeId)
}

/**
 * 按已知包路径解析仓库 Puppy occurrence 定位路径。
 *
 * `assemblyNodeId` 允许包含 `/`，因此只能按精确的 packageFileId 前缀拆分，禁止按最后一个斜杠猜测。
 *
 * @param nodeWhere `_assembly.json` 保存的仓库定位路径。
 * @param packageFileId 已知 `.puppy` 文件路径。
 * @returns 命中精确前缀时返回完整 assemblyNodeId，否则返回 null。
 */
export function parseRepositoryPuppyNodeWhere(
  nodeWhere: string,
  packageFileId: string,
): string | null {
  return parseRepositoryPuppyNodeWherePath(nodeWhere, packageFileId)
}

/**
 * 为装配工作文档中被同次导出命中的外层一级 child 写入仓库 Puppy occurrence 路径。
 * 未命中的外层一级 child 不属于本次导出范围，必须保留原有定位信息，交由后续加载链路判断是否新增。
 *
 * @param input 工作文档、运行根身份、包路径和同次导出 provenance。
 * @returns 已深克隆且完成 node_where 桥接的新文档。
 */
export function createAssemblyDocumentWithRepositoryPuppyNodeWhere(input: {
  readonly document: AssemblyDocument
  readonly rootNodeKey: string
  readonly packageFileId: string
  readonly identities: readonly RepositoryPuppyRootOccurrenceIdentity[]
}): AssemblyDocument {
  const next = cloneAssemblyDocument(input.document)
  const normalizedPackageFileId = normalizeRepositoryPackageFileId(input.packageFileId)
  const directChildren = listAssemblyDocumentDirectChildNodes(next, input.rootNodeKey)
  const directChildNodeKeys = new Set(directChildren.map((child) => child.nodeKey))
  const identityBySourceNodeKey = new Map<string, RepositoryPuppyRootOccurrenceIdentity>()

  input.identities.forEach((identity) => {
    if (!directChildNodeKeys.has(identity.sourceNodeKey)) {
      throw new Error(
        `repository puppy provenance 引用未知外层一级 child: sourceNodeKey=${identity.sourceNodeKey}, packageFileId=${normalizedPackageFileId}`,
      )
    }
    if (identityBySourceNodeKey.has(identity.sourceNodeKey)) {
      throw new Error(`repository puppy provenance sourceNodeKey 重复: sourceNodeKey=${identity.sourceNodeKey}`)
    }
    identityBySourceNodeKey.set(identity.sourceNodeKey, identity)
  })

  const nodeWhereBySourceNodeKey = new Map<string, string>()
  identityBySourceNodeKey.forEach((identity) => {
    nodeWhereBySourceNodeKey.set(
      identity.sourceNodeKey,
      createRepositoryPuppyNodeWhere(normalizedPackageFileId, identity.assemblyNodeId),
    )
  })

  if (isAssemblyPersistedDocument(next)) {
    return {
      ...next,
      children: next.children.map((child, index) => {
        const directChild = directChildren[index]
        if (!directChild) {
          throw new Error(
            `repository puppy persisted direct child 缺失: sourceNodeKey=<missing>, packageFileId=${normalizedPackageFileId}, index=${index}`,
          )
        }
        const nodeWhere = nodeWhereBySourceNodeKey.get(directChild.nodeKey)
        if (!nodeWhere) {
          return child
        }
        return {
          ...child,
          node_where: nodeWhere,
        }
      }),
    }
  }

  const nextNodesByKey = { ...next.nodesByKey }
  nodeWhereBySourceNodeKey.forEach((nodeWhere, sourceNodeKey) => {
    const childNode = next.nodesByKey[sourceNodeKey]
    if (!childNode) {
      throw new Error(
        `repository puppy indexed direct child 缺失: sourceNodeKey=${sourceNodeKey}, packageFileId=${normalizedPackageFileId}`,
      )
    }
    nextNodesByKey[sourceNodeKey] = {
      ...childNode,
      nodeWhere,
    }
  })
  return {
    ...next,
    nodesByKey: nextNodesByKey,
  }
}
